import { type FormEvent, useId, useState } from "react";

import { siteCopy } from "../../content";
import {
  NEWSLETTER_FORM_NAME,
  newsletterSubmissionSchema,
} from "../../lib/intake-schema";
import "./contact-forms.css";
import { submitNetlifyForm } from "./netlify-forms";

const copy = siteCopy.newsletter;

type SubmissionStatus = "idle" | "submitting" | "success" | "error";

export type NewsletterFormProps = {
  privacyUrl?: string;
  onSuccess?: () => void;
};

export function NewsletterForm({
  privacyUrl = "/privacy",
  onSuccess,
}: NewsletterFormProps) {
  const idPrefix = useId();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [newsletterConsent, setNewsletterConsent] = useState(false);
  const [status, setStatus] = useState<SubmissionStatus>("idle");
  const [statusMessage, setStatusMessage] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const fields = {
      "form-name": NEWSLETTER_FORM_NAME,
      first_name: firstName.trim() || undefined,
      email: email.trim(),
      newsletter_consent: newsletterConsent ? "yes" : "no",
      website,
    };
    const parsed = newsletterSubmissionSchema.safeParse(fields);

    if (!parsed.success) {
      setStatus("error");
      setStatusMessage(
        "Controleer je e-mailadres en bevestig je keuze. Er is nog niets verstuurd.",
      );
      return;
    }

    setStatus("submitting");
    setStatusMessage("Je aanmelding wordt verstuurd…");

    try {
      await submitNetlifyForm(fields);
      setFirstName("");
      setEmail("");
      setWebsite("");
      setNewsletterConsent(false);
      setStatus("success");
      setStatusMessage("Dank je. Je nieuwsbriefkeuze is ontvangen.");
      onSuccess?.();
    } catch {
      setStatus("error");
      setStatusMessage(
        "Aanmelden lukte niet. Probeer het straks opnieuw; er is nog niets bevestigd.",
      );
    }
  }

  if (status === "success") {
    return (
      <section className="contact-form contact-form--success" aria-live="polite">
        <h3>{copy.successTitle}</h3>
        <p>{statusMessage}</p>
      </section>
    );
  }

  const isSubmitting = status === "submitting";

  return (
    <form
      className="contact-form contact-form--compact"
      name={NEWSLETTER_FORM_NAME}
      method="post"
      data-netlify="true"
      data-netlify-honeypot="website"
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      noValidate
    >
      <input type="hidden" name="form-name" value={NEWSLETTER_FORM_NAME} />

      <div className="contact-form__intro">
        <p className="contact-form__eyebrow">{copy.eyebrow}</p>
        <h3>{copy.title}</h3>
        <p>{copy.intro}</p>
      </div>

      <div className="contact-form__field">
        <label htmlFor={`${idPrefix}-newsletter-first-name`}>
          Voornaam (optioneel)
        </label>
        <input
          id={`${idPrefix}-newsletter-first-name`}
          name="first_name"
          type="text"
          autoComplete="given-name"
          maxLength={80}
          value={firstName}
          onChange={(event) => setFirstName(event.currentTarget.value)}
        />
      </div>

      <div className="contact-form__field">
        <label htmlFor={`${idPrefix}-newsletter-email`}>E-mailadres</label>
        <input
          id={`${idPrefix}-newsletter-email`}
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          maxLength={254}
          required
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
        />
      </div>

      <label className="contact-form__check">
        <input
          name="newsletter_consent"
          type="checkbox"
          value="yes"
          required
          checked={newsletterConsent}
          onChange={(event) =>
            setNewsletterConsent(event.currentTarget.checked)
          }
        />
        <span>
          <strong>{copy.consentTitle}</strong>
          <small>{copy.consentDetail}</small>
        </span>
      </label>

      <p className="contact-form__privacy">
        Lees hoe je gegevens worden gebruikt in de{" "}
        <a href={privacyUrl}>privacyverklaring</a>.
      </p>

      <div className="contact-form__honeypot" aria-hidden="true">
        <label htmlFor={`${idPrefix}-newsletter-website`}>
          Laat dit veld leeg
        </label>
        <input
          id={`${idPrefix}-newsletter-website`}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(event) => setWebsite(event.currentTarget.value)}
        />
      </div>

      <button
        className="contact-form__submit"
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Even aanmelden…" : copy.submitLabel}
      </button>

      <p
        className={`contact-form__status contact-form__status--${status}`}
        role={status === "error" ? "alert" : "status"}
        aria-live={status === "error" ? "assertive" : "polite"}
      >
        {statusMessage}
      </p>
    </form>
  );
}
